import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

const DESKTOP_QUERY = '(min-width: 1024px)'
const MOBILE_LANDSCAPE_QUERY = '(max-width: 1023px) and (orientation: landscape)'
const MOBILE_PORTRAIT_QUERY = '(max-width: 1023px) and (orientation: portrait)'

export type AppLayoutMode = 'desktop' | 'mobile-landscape' | 'mobile-portrait'

interface AppViewportContextValue {
  isDesktop: boolean
  isMobileLandscape: boolean
  isMobilePortrait: boolean
  layoutMode: AppLayoutMode
}

const AppViewportContext = createContext<AppViewportContextValue>({
  isDesktop: true,
  isMobileLandscape: false,
  isMobilePortrait: false,
  layoutMode: 'desktop',
})

export const AppViewportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [viewportState, setViewportState] = useState<{
    isDesktop: boolean
    isMobileLandscape: boolean
    isMobilePortrait: boolean
  }>(() =>
    typeof window !== 'undefined'
      ? {
          isDesktop: window.matchMedia(DESKTOP_QUERY).matches,
          isMobileLandscape: window.matchMedia(MOBILE_LANDSCAPE_QUERY).matches,
          isMobilePortrait: window.matchMedia(MOBILE_PORTRAIT_QUERY).matches,
        }
      : {
          isDesktop: true,
          isMobileLandscape: false,
          isMobilePortrait: false,
        },
  )

  useEffect(() => {
    const desktopMq = window.matchMedia(DESKTOP_QUERY)
    const mobileLandscapeMq = window.matchMedia(MOBILE_LANDSCAPE_QUERY)
    const mobilePortraitMq = window.matchMedia(MOBILE_PORTRAIT_QUERY)
    const update = () =>
      setViewportState({
        isDesktop: desktopMq.matches,
        isMobileLandscape: mobileLandscapeMq.matches,
        isMobilePortrait: mobilePortraitMq.matches,
      })

    update()
    desktopMq.addEventListener('change', update)
    mobileLandscapeMq.addEventListener('change', update)
    mobilePortraitMq.addEventListener('change', update)
    return () => {
      desktopMq.removeEventListener('change', update)
      mobileLandscapeMq.removeEventListener('change', update)
      mobilePortraitMq.removeEventListener('change', update)
    }
  }, [])

  const value = useMemo(() => {
    const layoutMode: AppLayoutMode = viewportState.isDesktop
      ? 'desktop'
      : viewportState.isMobileLandscape
        ? 'mobile-landscape'
        : 'mobile-portrait'

    return {
      ...viewportState,
      layoutMode,
    }
  }, [viewportState])

  return <AppViewportContext.Provider value={value}>{children}</AppViewportContext.Provider>
}

export function useAppViewport() {
  return useContext(AppViewportContext)
}
